import { Col, Row } from "react-bootstrap";
import {
  competitionGroupsMasterDataApi,
  sportsMasterDataApi,
} from "@/apis/apis";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";

import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import { CompetitionGroupInitialValues } from "@/data/WebDataManagement/MasterData/CompetitionGroups/initial-values";
import CompetitionGroupSchema from "@/schema/WebDataManagement/MasterData/CompetitionGroups/CompetitionGroupSchema";
import ImageInput from "@/components/ImageInput/Index";
import { LIST_MODE } from "@/constant/Lists/listMode";
import LocalizedInput from "@/components/LocalizedInput";
import RenderToast from "@/components/RenderToast";
import StatusInput from "@/components/StatusInput";
import axiosInstance from "@/Axios/ConfiguredAxios";
import { debounce } from "@mui/material";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { useSelector } from "react-redux";

const CompetitionGroupModal = forwardRef((_, ref) => {
  const [sportId, setSportId] = useState(null);
  const [parentId, setParentId] = useState(null);
  const [defaultSports, setDefaultSports] = useState([]);
  const [defaultParentCompetitionGroups, setDefaultParentCompetitionGroups] =
    useState([]);
  const [modalState, setModalState] = useState({ isOpen: false, id: null });
  const { accessToken } = useSelector((state) => state?.authReducer);

  const [loading, setLoading] = useState({
    parentCompetitionGroupsList: false,
    sportsList: false,
  });

  const showApi = modalState.id
    ? `${competitionGroupsMasterDataApi}/${modalState.id}`
    : null;
  const {
    data: showData,
    isSuccess: showIsSuccess,
    error: showError,
  } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });
  useEffect(() => {
    if (showIsSuccess) {
      setSportId(showData.data.sport.id);
      setParentId(showData.data.parent.id);
    }
  }, [showIsSuccess, showData]);

  const locales = useSelector(
    (state) => state?.siteConfigurationReducer?.locales
  );
  const localeCodes = useMemo(
    () => locales?.map((locale) => locale.code) ?? [],
    [locales]
  );

  const {
    mutate: save,
    error: saveError,
    isPending: saveIsPending,
  } = usePost({
    apiURL: modalState.id
      ? `${competitionGroupsMasterDataApi}/${modalState.id}?_method=PUT`
      : competitionGroupsMasterDataApi,
    isFormData: true,
    onSuccess: (data) => {
      closeModal();
      RenderToast({
        type: "success",
        message: `Competition Group ${
          modalState.id ? "edited" : "added"
        } successfully.`,
      });
    },
  });

  const sportslistApi = `${sportsMasterDataApi}?mode=${LIST_MODE.SELECT}&filter[keyword]=`;
  const competitionGroupslistApi = `${competitionGroupsMasterDataApi}?mode=${LIST_MODE.SELECT}&filter[keyword]=`;

  useEffect(() => {
    if (!modalState.isOpen) return;

    const fetchDefaultSports = async () => {
      setLoading((prev) => ({ ...prev, sportsList: true }));
      const response = await axiosInstance.get(sportslistApi);

      if (response) {
        const options = response.data.data.map((data) => ({
          label: `${data.code} - ${data.name}`,
          value: data.id,
        }));

        const sportId = showData?.data?.sport?.id;
        if (sportId && !options.some((opt) => opt.value === sportId)) {
          // Add the sport to the top of the options list
          options.unshift({
            label: `${showData.data.sport.code} - ${showData.data.sport.name}`,
            value: showData.data.sport.id,
          });
        }

        options.unshift({ label: "No sport selected", value: null });
        setDefaultSports(options);
      }

      setLoading((prev) => ({ ...prev, sportsList: false }));
    };
    const fetchDefaultParentCompetitionGroups = async () => {
      setLoading((prev) => ({ ...prev, parentCompetitionGroupsList: true }));
      const response = await axiosInstance.get(competitionGroupslistApi);

      if (response) {
        const options =
          response.data.data
            .filter((val) => val.id !== modalState.id) // Do not display the selected id
            .map((val) => ({
              label: `${val.code} - ${val.name}`,
              value: val.id,
            })) ?? [];

        const parentId = showData?.data?.parent?.id;
        if (parentId && !options.some((opt) => opt.value === parentId)) {
          // Add the parent to the top of the options list
          options.unshift({
            label: `${showData.data.parent.code} - ${showData.data.parent.name}`,
            value: showData.data.parent.id,
          });
        }

        options.unshift({ label: "No parent selected", value: null });
        setDefaultParentCompetitionGroups(options);
      }

      setLoading((prev) => ({ ...prev, parentCompetitionGroupsList: false }));
    };

    fetchDefaultSports();
    fetchDefaultParentCompetitionGroups();
  }, [
    accessToken,
    competitionGroupslistApi,
    modalState,
    showData,
    sportslistApi,
  ]);

  const debouncedFetchSports = debounce(async (input, callback) => {
    setLoading((prev) => ({ ...prev, sportsList: true }));
    const response = await axiosInstance.get(`${sportslistApi}${input}`);

    if (response) {
      callback(
        response.data.data.map((item) => ({
          label: `${item.code} - ${item.name}`,
          value: item.id,
        })) ?? []
      );
    }

    setLoading((prev) => ({ ...prev, sportsList: false }));
  }, 500);

  const debouncedFetchParentCompetitionGroups = debounce(
    async (input, callback) => {
      setLoading((prev) => ({ ...prev, parentCompetitionGroupsList: true }));
      const response = await axiosInstance.get(
        `${competitionGroupslistApi}${input}`
      );

      if (response) {
        callback(
          response.data.data.map((item) => ({
            label: `${item.code} - ${item.name}`,
            value: item.id,
          })) ?? []
        );
      }

      setLoading((prev) => ({ ...prev, parentCompetitionGroupsList: false }));
    },
    500
  );

  const getSportSelectedOption = () => {
    // doesnt select anything on purpose
    if (sportId === null && sportId !== formik.initialValues.sport_id)
      return null;

    return sportId
      ? defaultSports.find((opt) => opt.value === sportId)
      : showData?.data?.sport?.code // select the showed data value
      ? {
          label: `${showData?.data?.sport?.code} - ${showData?.data?.sport?.name}`,
          value: sportId,
        }
      : null;
  };
  const getParentCompetitionGroupSelectedOption = () => {
    // doesnt select anything on purpose
    if (parentId === null && parentId !== formik.initialValues.parent_id)
      return null;

    return parentId
      ? defaultParentCompetitionGroups.find((opt) => opt.value === parentId) // select the value
      : showData?.data?.parent?.code // select the showed data value
      ? {
          label: `${showData?.data?.parent?.code} - ${showData?.data?.parent?.name}`,
          value: parentId,
        }
      : null;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: CompetitionGroupInitialValues(
      localeCodes,
      modalState.id ? showData : null
    ),
    validationSchema: CompetitionGroupSchema(localeCodes, !!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    formData.append("sport_id", values.sport_id);
    if (values.parent_id) {
      formData.append("parent_id", values.parent_id);
    }
    formData.append("code", values.code);
    formData.append("status", values.status);
    Object.keys(values.localizations).forEach((key) => {
      if (values.localizations[key]) {
        formData.append(`localizations[${key}]`, values.localizations[key]);
      }
    });
    if (values.icon) {
      formData.append("icon", values.icon);
    }

    save(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    setSportId(null);
    setParentId(null);
    setModalState({ isOpen: false, id: null });
  }, [formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={
        modalState.id ? "Edit Competition Group" : "Add Competition Group"
      }
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Sport ID"
            placeholder="No sport selected. Type to search..."
            defaultOptions={defaultSports}
            loadOptions={debouncedFetchSports}
            isLoading={loading.sportsList}
            value={getSportSelectedOption()}
            onChange={(selected) => {
              setSportId(selected?.value || null);
              formik.setFieldValue("sport_id", selected?.value || null);
            }}
            errorText={
              (formik.touched.sport_id && formik.errors.sport_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.sport_id?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            required={false}
            label="Parent ID"
            placeholder="No parent selected. Type to search..."
            defaultOptions={defaultParentCompetitionGroups}
            loadOptions={debouncedFetchParentCompetitionGroups}
            isLoading={loading.parentCompetitionGroupsList}
            value={getParentCompetitionGroupSelectedOption()}
            onChange={(selected) => {
              setParentId(selected?.value || null);
              formik.setFieldValue("parent_id", selected?.value || null);
            }}
            errorText={
              (formik.touched.parent_id && formik.errors.parent_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.parent_id?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"code"}
            saveError={saveError}
            label="ID"
          />
        </Col>
      </Row>

      <LocalizedInput
        formik={formik}
        error={saveError}
        isEdit={!!modalState.id}
      />

      <Row>
        <Col md={12}>
          <ImageInput
            id="icon"
            formFormik={formik}
            fieldName="icon"
            label="Icon"
            error={saveError}
            existingImageUrl={modalState.id && showData?.data?.icon_url}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default CompetitionGroupModal;
